import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Path } from '@shared/model/dataset';
import { Alert } from '../components/Alert';
import { ItineraryForm } from '../components/ItineraryForm';
import { Results } from '../components/Results';
import { TrailMap } from '../components/LazyTrailMap';
import { useDataset } from '../hooks/useDataset';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useHashItinerary } from '../hooks/useHashItinerary';
import { useItineraries } from '../hooks/useItineraries';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { pageTitle, SITE_NAME, type PageConfig } from '../site';
import styles from './ItineraryPage.module.css';

interface Props {
    page: PageConfig;
}

export function ItineraryPage({ page }: Props) {
    const { status, error, graph, dataset } = useDataset();
    const [hash, setHash] = useHashItinerary();
    // Selection while only one of the two points is chosen; once both are, the URL fragment is the state.
    const [pending, setPending] = useState<{
        from: string | null;
        to: string | null;
    }>({ from: null, to: null });
    const from = hash ? hash.from : pending.from;
    const to = hash ? hash.to : pending.to;
    const [hideClosed, setHideClosed] = useLocalStorage('checkHideClosed', true);
    const { state, search, reset } = useItineraries(graph, dataset);
    const [activeIds, setActiveIds] = useState<ReadonlySet<string>>(new Set());

    const groups = useMemo(() => dataset?.pointsByArea(page.area) ?? new Map(), [dataset, page.area]);
    const start = from && dataset ? dataset.getPoint(from) : undefined;
    const end = to && dataset ? dataset.getPoint(to) : undefined;

    // The URL fragment drives the computation (back button, bookmarks, default itinerary of the page).
    useEffect(() => {
        if (!dataset) return;
        if (hash) {
            if (dataset.getPoint(hash.from) && dataset.getPoint(hash.to)) search(hash.from, hash.to);
        } else if (page.defaultStart && page.defaultEnd) {
            // replace: the back button leaves the page instead of landing on the bare URL that redirects again
            setHash({ from: page.defaultStart, to: page.defaultEnd }, { replace: true });
        } else {
            reset();
        }
    }, [dataset, hash, page.defaultStart, page.defaultEnd, search, setHash, reset]);

    useDocumentTitle(
        start && end && state.status !== 'idle'
            ? `Itinéraire de ${start.name} à ${end.name} - ${SITE_NAME}`
            : pageTitle(page),
    );

    const select = useCallback(
        (nextFrom: string | null, nextTo: string | null) => {
            if (nextFrom && nextTo) setHash({ from: nextFrom, to: nextTo });
            else setPending({ from: nextFrom, to: nextTo });
        },
        [setHash],
    );

    const toggle = useCallback((path: Path) => {
        setActiveIds((current) => {
            const next = new Set(current);
            if (next.has(path.id)) next.delete(path.id);
            else next.add(path.id);
            return next;
        });
    }, []);
    const clear = useCallback(() => setActiveIds(new Set()), []);
    const onNewResults = useCallback((first: Path | undefined) => setActiveIds(new Set(first ? [first.id] : [])), []);

    const activePaths = useMemo(
        () => (state.status === 'done' ? state.paths.filter((p) => activeIds.has(p.id)) : []),
        [state, activeIds],
    );

    return (
        <div className={`container ${styles.page}`}>
            {page.hero && (
                <div className={`container container--narrow ${styles.hero}`}>
                    <h1>Votre itinéraire de randonnée à la Réunion en toute simplicité</h1>
                    <p>
                        <b>Fini les calculs interminables d'itinéraires.</b> Ce calculateur d'itinéraire prend en compte
                        tous les sentiers ouverts et fermés par l'ONF à la Réunion pour vous proposer en toute sécurité
                        et simplicité des itinéraires de randonnées.
                    </p>
                    <div className={styles.features}>
                        <div>
                            <b>Pratique</b>
                            <br />
                            Tous les sentiers ONF de la Réunion sont sur Tipa.re
                        </div>
                        <div>
                            <b>Simple &amp; rapide</b>
                            <br />
                            Calcul automatique des itinéraires possibles
                        </div>
                        <div>
                            <b>En sécurité</b>
                            <br />
                            Une mise à jour quotidienne des sentiers fermés
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.columns}>
                <div className={styles.left}>
                    {status === 'error' && <Alert level="danger">Impossible de charger les sentiers : {error}</Alert>}
                    <ItineraryForm
                        groups={groups}
                        from={from}
                        to={to}
                        onFromChange={(id) => select(id, to)}
                        onToChange={(id) => select(from, id)}
                        onSwap={() => select(to, from)}
                        hideClosed={hideClosed}
                        onHideClosedChange={setHideClosed}
                    />
                    <Results
                        state={state}
                        start={start}
                        end={end}
                        hideClosed={hideClosed}
                        activeIds={activeIds}
                        onToggle={toggle}
                        onClear={clear}
                        onNewResults={onNewResults}
                    />
                </div>
                <div className={styles.right}>
                    <div className={styles.sticky}>
                        <TrailMap
                            center={page.center}
                            zoom={page.zoom}
                            paths={activePaths}
                            showAllRoutes={!!page.showAllRoutes && state.status === 'idle'}
                            showAllPoints={!!page.showAllRoutes && state.status === 'idle'}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
